// src/modules/app-views/invoices/service.js
// *******************************************************************************
// INVOICES SERVICE
// *******************************************************************************
// Service layer for invoice operations including CRUD, emission, and fiscal
// document management (NFe, NFCe, NFSe).
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import axiosService from 'services/axiosService';
import { API } from 'constants/Api';
import appService from 'services/appService';
import translateX from 'utils/translateX';
import { PRINTER_CONFIG, ESC_POS, encodeCP850 } from 'constants/PrinterConstants';
import { fromAuthStore } from 'stores/auth';
import { getStorageModuleItem } from 'stores/module';

// *******************************************************************************
// API METHODS
// *******************************************************************************

/**
 * Get invoice by ID
 * @param {string} invoiceId - Invoice ID
 * @returns {Promise<Object>} Invoice data
 */
async function getById(invoiceId) {
  const url = API.INVOICES.URL.replace(':_id?', invoiceId);

  const [response] = await axiosService.get({
    url,
    returnAsObject: true,
  });

  return response;
}

/**
 * Get invoices with pagination
 * @param {Object} params - Pagination params
 * @param {number} [params.page=1] - Page number
 * @param {number} [params.size=10] - Page size
 * @param {string} [params.textSearch] - Search text
 * @param {Object} [params.filters] - Additional filters
 * @returns {Promise<Object>} Paginated response
 */
async function getPaginated(params = {}) {
  const [response] = await axiosService.get({
    url: API.INVOICES.PAGINATION,
    params,
    returnAsObject: true,
  });

  return response;
}

/**
 * Create new invoice
 * @param {Object} data - Invoice data
 * @returns {Promise<Object>} Created invoice
 */
async function create(data) {
  const url = API.INVOICES.URL.replace(':_id?', '');

  const [response] = await axiosService.post({
    url,
    data,
    returnAsObject: true,
  });

  return response;
}

/**
 * Update invoice
 * @param {string} invoiceId - Invoice ID
 * @param {Object} data - Invoice data
 * @returns {Promise<Object>} Updated invoice
 */
async function update(invoiceId, data) {
  const url = API.INVOICES.URL.replace(':_id?', invoiceId);

  const [response] = await axiosService.put({
    url,
    data,
    returnAsObject: true,
  });

  return response;
}

/**
 * Delete invoice (soft delete)
 * @param {string} invoiceId - Invoice ID
 * @returns {Promise<Object>} Delete result
 */
async function remove(invoiceId) {
  const url = API.INVOICES.URL.replace(':_id?', invoiceId);

  const [response] = await axiosService.delete({
    url,
    returnAsObject: true,
  });

  return response;
}

// *******************************************************************************
// FISCAL OPERATIONS
// *******************************************************************************

/**
 * Emit NFCe - delegates to generic emit()
 * @deprecated Use emit() directly
 */
async function emitNfce(invoiceId, payload = {}) {
  return emit(invoiceId, 'NFCE', payload);
}

/**
 * Emit NFe - delegates to generic emit()
 * @deprecated Use emit() directly
 */
async function emitNfe(invoiceId, payload = {}) {
  return emit(invoiceId, 'NFE', payload);
}

/**
 * Emit NFSe (Nota Fiscal de Serviço Eletrônica)
 * @param {string} invoiceId - Invoice ID to emit
 * @returns {Promise<Object>} Emission result with protocol, etc.
 */
async function emitNfse(invoiceId) {
  const msg = await appService.message({
    flag: 'loading',
    text: 'emitting_nfse',
    delay: 0,
  });

  try {
    const [response] = await axiosService.post({
      url: API.INVOICES.EMIT_NFSE,
      data: { invoiceId },
      returnAsObject: true,
    });

    if (response?.success) {
      msg.update({
        flag: 'success',
        text: translateX('nfse_emitted_success'),
        delay: 3,
      });
    } else {
      msg.update({
        flag: 'error',
        text: response?.message || translateX('nfse_emission_failed'),
        delay: 5,
      });
    }

    return response;
  } catch (error) {
    msg.update({
      flag: 'error',
      text: error?.message || translateX('nfse_emission_error'),
      delay: 5,
    });
    throw error;
  }
}

/**
 * Emit invoice with optional CPF (for Nota Fiscal Paulista)
 * Generic emit method that calls the batch emit API
 * @param {string} invoiceId - Invoice ID to emit
 * @param {string} documentType - Document type (NFCE, NFE, NFSE)
 * @param {Object} payload - Additional payload
 * @param {string} [payload.cpf] - Optional CPF for Nota Fiscal Paulista
 * @returns {Promise<Object>} Emission result
 */
async function emit(invoiceId, documentType, payload = {}) {
  try {
    const [response] = await axiosService.post({
      url: API.INVOICES.EMIT,
      data: {
        ids: [invoiceId],
        attachDanfeXml: true,
        cpf: payload.cpf || null,
        environment: payload.environment || null,
      },
      returnAsObject: true,
    });

    // Process batch results
    const results = response?.results || [];
    const result = results[0] || {};

    return {
      success: result.success,
      message: result.message,
      error: result.error,
      status: result.status,
      errorList: result.errorList,
      documentNumber: result.documentNumber,
      documentSerie: result.documentSerie,
      accessKey: result.accessKey,
      protocol: result.protocol,
      itemsCount: result.itemsCount,
      recipientName: result.recipientName,
      orderLegacyCode: result.orderLegacyCode,
    };
  } catch (error) {
    console.error('[invoicesService.emit] Error:', error);

    // Extract error message from axios error response (400 errors from backend)
    const errorMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      'Unknown error';

    // Return error object instead of throwing to allow proper handling
    return {
      success: false,
      error: errorMessage,
      status: error?.response?.status || 500,
      code: error?.response?.data?.code,
    };
  }
}

/**
 * Cancel invoice
 * @param {string} invoiceId - Invoice ID to cancel
 * @param {string} justification - Cancellation reason (min 15 chars)
 * @returns {Promise<Object>} Cancellation result
 */
async function cancel(invoiceId, justification) {
  const msg = await appService.message({
    flag: 'loading',
    text: 'cancelling_invoice',
    delay: 0,
  });

  try {
    const url = API.INVOICES.CANCEL.replace(':invoiceId', invoiceId);

    const [response] = await axiosService.post({
      url,
      data: { justification },
      returnAsObject: true,
    });

    if (response?.success) {
      msg.update({
        flag: 'success',
        text: translateX('invoice_canceled_success'),
        delay: 3,
      });
    } else {
      msg.update({
        flag: 'error',
        text: response?.message || translateX('invoice_cancellation_failed'),
        delay: 5,
      });
    }

    return response;
  } catch (error) {
    msg.update({
      flag: 'error',
      text: error?.message || translateX('invoice_cancellation_error'),
      delay: 5,
    });
    throw error;
  }
}

/**
 * Get invoice PDF (DANFE)
 * @param {string} invoiceId - Invoice ID
 * @returns {Promise<Blob>} PDF blob
 */
async function getPdf(invoiceId) {
  const url = API.INVOICES.GET_PDF.replace(':_id', invoiceId);

  const response = await axiosService.get({
    url,
    responseType: 'blob',
  });

  return response;
}

/**
 * Get invoice XML
 * @param {string} invoiceId - Invoice ID
 * @returns {Promise<string>} XML string
 */
async function getXml(invoiceId) {
  const url = API.INVOICES.GET_XML.replace(':_id', invoiceId);

  const [response] = await axiosService.get({
    url,
    returnAsObject: true,
  });

  return response;
}

/**
 * Download DANFE (PDF)
 * @param {string} invoiceId - Invoice ID
 * @param {string} [filename] - Optional filename
 */
async function downloadDanfe(invoiceId, filename) {
  return downloadFiscalFile({
    apiPath: API.INVOICES.GET_DANFE.replace(':_id', invoiceId),
    filename: filename || `danfe-${invoiceId}.pdf`,
    loadingText: 'downloading_danfe',
    successText: 'danfe_downloaded',
    errorText: 'danfe_download_error',
  });
}

/**
 * Download XML (authorized/cancellation XML)
 * @param {string} invoiceId - Invoice ID
 * @param {string} [filename] - Optional filename
 */
async function downloadXml(invoiceId, filename) {
  return downloadFiscalFile({
    apiPath: API.INVOICES.GET_XML.replace(':_id', invoiceId),
    filename: filename || `nota-${invoiceId}.xml`,
    loadingText: 'downloading_xml',
    successText: 'xml_downloaded',
    errorText: 'xml_download_error',
  });
}

/**
 * Fetch a fiscal file (PDF/XML) as a blob and trigger a browser download.
 * Uses native fetch — axiosService transforms the body to JSON and returns an
 * array [docs, result] (no .data blob), which corrupted the download.
 * Mirrors the authenticated fetch pattern used by exportXmlBatch.
 */
async function downloadFiscalFile({ apiPath, filename, loadingText, successText, errorText }) {
  const msg = await appService.message({ flag: 'loading', text: loadingText, delay: 0 });

  try {
    const useZustand = (await import('hooks/useZustand')).default;
    const token = useZustand('auth', 'token', false);
    const subscription = useZustand('auth', 'subscription', false);
    const accountId = useZustand('auth', 'accountId', false);

    if (!token) {
      throw new Error('No authentication token found');
    }

    const { default: appCfg } = await import('configs/ConfigIndex');
    const { getUrlFromBrowser } = await import('utils/helpers');

    const isLocal = appCfg.CONNECTION === 'local';
    const protocol = isLocal ? 'http' : 'https';
    const host = isLocal ? appCfg.API_HOST_LOCALHOST : appCfg.API_HOST_PRODUCTION;
    const port = appCfg.HOMOLOGATION_MODE
      ? appCfg.API_PORT_HOMOLOGATION
      : appCfg.API_PORT_PRODUCTION;
    const baseUrl = `${protocol}://${host}:${port}/api`;

    const response = await fetch(`${baseUrl}${apiPath}`, {
      method: 'GET',
      headers: {
        Authorization: token,
        'x-application': appCfg.ID,
        'x-token': appCfg.TOKEN,
        'x-clienturl': getUrlFromBrowser(),
        'x-accountid': accountId || '',
        'x-subscriptionid': subscription?._id || '',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const blob = await response.blob();
    // Pick the extension from the response content-type (a CORS-safelisted
    // header, always readable cross-origin — unlike Content-Disposition).
    // NF-e DANFE = PDF, NFC-e DANFE = HTML (FocusNFe), XML = XML.
    const ct = (blob.type || response.headers.get('content-type') || '').toLowerCase();
    const base = filename.replace(/\.[^.]+$/, '');
    let finalName = filename;
    if (ct.includes('html')) finalName = `${base}.html`;
    else if (ct.includes('xml')) finalName = `${base}.xml`;
    else if (ct.includes('pdf')) finalName = `${base}.pdf`;

    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = finalName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);

    msg.update({ flag: 'success', text: translateX(successText), delay: 2 });
  } catch (error) {
    msg.update({ flag: 'error', text: error?.message || translateX(errorText), delay: 5 });
    throw error;
  }
}

/**
 * Wait for a child element matching `selector` to appear inside `root`.
 * @param {HTMLElement} root - Container to search within
 * @param {string} selector - CSS selector
 * @param {number} timeout - Max wait in ms
 * @returns {Promise<HTMLElement>}
 */
function waitForChild(root, selector, timeout = 2000) {
  return new Promise((resolve, reject) => {
    const start = performance.now();
    const tick = () => {
      const el = root.querySelector(selector);
      if (el) return resolve(el);
      if (performance.now() - start > timeout) {
        return reject(new Error(`timeout waiting for ${selector}`));
      }
      requestAnimationFrame(tick);
    };
    tick();
  });
}

/**
 * Open the browser print dialog for an NFC-e DANFE so the user can "Save as PDF".
 * FocusNFe only serves the NFC-e DANFE as HTML (no PDF). We render the app's own
 * DANFE (the exact layout used for printing) off-screen, then hand it to the
 * proven browser-print pipeline (printDanfeBrowser) which clones it, converts the
 * QR Code SVG to an image, builds a self-contained print document and opens the
 * print dialog. The user picks "Salvar como PDF" → crisp vector PDF.
 * No html2canvas (which froze/blanked on the large issuer logo bitmap).
 * @param {Object} data - Full invoice document
 */
async function printNfceDanfeToPdf(data) {
  const msg = await appService.message({ flag: 'loading', text: 'downloading_danfe', delay: 0 });

  let container;
  let root;

  try {
    // Lazy/dynamic imports avoid any static circular dependency with the
    // React DANFE component tree.
    const [React, { createRoot }, { ConfigProvider }, { lightTheme }, { default: PreviewNFCeStep }] =
      await Promise.all([
        import('react'),
        import('react-dom/client'),
        import('antd'),
        import('configs/appEngine'),
        import('./pages/Init/modals/PreviewNFCeModal/Step1.jsx'),
      ]);

    // 1. Render the DANFE off-screen (outside any transformed/scrolled ancestor)
    container = document.createElement('div');
    container.style.cssText = 'position:fixed;left:-99999px;top:0;width:380px;background:#fff;';
    document.body.appendChild(container);

    root = createRoot(container);
    root.render(
      React.createElement(
        ConfigProvider,
        { theme: lightTheme },
        React.createElement(PreviewNFCeStep, { data })
      )
    );

    // 2. Wait for the DANFE container (+ QR best-effort) to paint
    const source = await waitForChild(container, '#danfe-modal-print-container', 3000);
    await waitForChild(container, '.danfe-qrcode svg', 1500).catch(() => {});
    await new Promise(r => setTimeout(r, 150));

    // 3. Reuse the proven browser-print pipeline on the rendered DANFE
    if (!source.id) source.id = 'danfe-modal-print-container';
    await printDanfeBrowser(source.id);

    msg.update({ flag: 'success', text: translateX('danfe_downloaded'), delay: 2 });
  } catch (error) {
    msg.update({
      flag: 'error',
      text: error?.message || translateX('danfe_download_error'),
      delay: 5,
    });
    throw error;
  } finally {
    // Keep the off-screen render alive briefly so printDanfeBrowser's clone/iframe
    // pipeline can finish reading from it before we unmount.
    setTimeout(() => {
      try {
        root?.unmount();
      } catch {
        /* noop */
      }
      container?.remove();
    }, 2000);
  }
}

// *******************************************************************************
// BATCH OPERATIONS
// *******************************************************************************

/**
 * Archive multiple invoices
 * @param {string[]} invoiceIds - Array of invoice IDs
 * @returns {Promise<Object>} Archive result
 */
async function archiveMany(invoiceIds) {
  const [response] = await axiosService.post({
    url: API.INVOICES.ADD_TO_SET,
    data: {
      ids: invoiceIds,
      field: 'status',
      value: 'archived',
    },
    returnAsObject: true,
  });

  return response;
}

/**
 * Delete multiple invoices
 * @param {string[]} invoiceIds - Array of invoice IDs
 * @returns {Promise<Object>} Delete result
 */
async function deleteMany(invoiceIds) {
  const url = API.INVOICES.URL.replace(':_id?', '');

  const [response] = await axiosService.delete({
    url,
    data: { ids: invoiceIds },
    returnAsObject: true,
  });

  return response;
}

// *******************************************************************************
// PRINT OPERATIONS
// *******************************************************************************

/**
 * Print DANFE NFC-e document
 * Automatically chooses between direct USB print or browser print dialog based on PRINTER_CONFIG
 * @param {string} elementId - DOM element ID containing the DANFE content
 * @param {Object} [options] - Print options
 * @param {boolean} [options.forceDirect] - Force direct USB print (ignore config)
 * @param {boolean} [options.forcePreview] - Force browser print dialog (ignore config)
 * @param {Object} [options.invoiceData] - Invoice data for direct ESC/POS printing
 * @returns {Promise<boolean>} Success status
 */
async function printDanfe(elementId = 'danfe-print-container', options = {}) {
  const { forceDirect, forcePreview, forceNetwork, invoiceData } = options;

  // Get print method from storage (nfcePrintMethod or nfePrintMethod)
  const printMethod = getStorageModuleItem('nfcePrintMethod') || 'browser';

  // Check if network printing should be used
  const useNetwork = forceNetwork || (!forcePreview && !forceDirect && printMethod === 'network');

  // If network printing is requested, use that
  if (useNetwork) {
    try {
      return await printDanfeNetwork(elementId);
    } catch (networkError) {
      console.warn('[PRINT] Network print failed, falling back to browser print:', networkError);
      appService.message({ flag: 'warning', text: 'network_print_failed_fallback', duration: 3 });
    }
  }

  // Determine print mode from config or options
  const useDirect = forceDirect || (!forcePreview && PRINTER_CONFIG.printMode === 'direct');

  // If direct printing is requested and we have invoice data, try direct printing
  if (useDirect && invoiceData) {
    // Try Web Serial API first (better macOS support), then WebUSB
    try {
      return await printDanfeSerial(invoiceData);
    } catch (serialError) {
      console.warn('[PRINT] Serial print failed:', serialError);

      try {
        return await printDanfeDirectUSB(invoiceData);
      } catch (usbError) {
        console.warn('[PRINT] USB print also failed, falling back to browser print:', usbError);
        // Fall back to browser print on error
      }
    }
  }

  // Default: use browser print dialog
  return printDanfeBrowser(elementId);
}

// Known thermal printer USB vendor IDs (for filtering serial ports)
const KNOWN_PRINTER_VENDORS = [
  { usbVendorId: 0x04b8 }, // Epson
  { usbVendorId: 0x0b1b }, // Bematech
  { usbVendorId: 0x0dd4 }, // Elgin
  { usbVendorId: 0x0519 }, // Star Micronics
  { usbVendorId: 0x0416 }, // Winbond (some thermal printers)
  { usbVendorId: 0x067b }, // Prolific (USB-Serial adapters often used with printers)
  { usbVendorId: 0x0403 }, // FTDI (USB-Serial adapters)
];

/**
 * Print DANFE via Web Serial API (better macOS support)
 * Attempts to reuse previously authorized ports before prompting user
 * @param {Object} invoiceData - Invoice data to print
 * @returns {Promise<boolean>} Success status
 */
async function printDanfeSerial(invoiceData) {
  // Check if Web Serial API is supported
  if (!navigator.serial) {
    throw new Error('Web Serial API not supported');
  }

  let port = null;

  try {
    // Step 1: Try to get previously authorized ports (no user interaction needed)
    const authorizedPorts = await navigator.serial.getPorts();

    if (authorizedPorts.length > 0) {
      // Use the first authorized port (most likely the previously selected printer)
      // Filter to find a port that looks like a printer (not Bluetooth)
      const printerPort = authorizedPorts.find(p => {
        const info = p.getInfo?.() || {};
        // Accept ports with known printer vendor IDs or USB modem ports (common for thermal printers)
        return (
          KNOWN_PRINTER_VENDORS.some(v => v.usbVendorId === info.usbVendorId) || !info.usbVendorId // Serial ports without vendor ID are usually USB-serial converters
        );
      });

      if (printerPort) {
        port = printerPort;
        console.log('[PRINT SERIAL] Using previously authorized port');
      }
    }

    // Step 2: If no authorized port found, request one with filters (only show printers)
    if (!port) {
      port = await navigator.serial.requestPort({
        filters: KNOWN_PRINTER_VENDORS,
      });
    }

    // Open port with common thermal printer settings
    await port.open({
      baudRate: PRINTER_CONFIG.serial.baudRate || 115200,
      dataBits: PRINTER_CONFIG.serial.dataBits || 8,
      stopBits: PRINTER_CONFIG.serial.stopBits || 1,
      parity: PRINTER_CONFIG.serial.parity || 'none',
      flowControl: PRINTER_CONFIG.serial.flowControl || 'none',
    });

    // Build ESC/POS commands
    const escPosData = buildDanfeEscPos(invoiceData);

    // Get writer and send data
    const writer = port.writable.getWriter();
    await writer.write(escPosData);
    writer.releaseLock();

    // Success message
    appService.message({ flag: 'success', text: 'print_sent_to_printer', duration: 2 });

    return true;
  } catch (error) {
    console.error('[PRINT SERIAL] Error:', error);

    if (error.name === 'NotFoundError') {
      appService.message({ flag: 'warning', text: 'printer_selection_canceled' });
    } else {
      appService.message({ flag: 'error', text: 'print_serial_error' });
    }

    throw error;
  } finally {
    // Close port
    if (port) {
      try {
        await port.close();
      } catch (e) {
        console.warn('[PRINT SERIAL] Error closing port:', e);
      }
    }
  }
}

/**
 * Print DANFE directly to USB thermal printer using ESC/POS commands
 * @param {Object} invoiceData - Invoice data to print
 * @returns {Promise<boolean>} Success status
 */
async function printDanfeDirectUSB(invoiceData) {
  // Check if WebUSB is supported
  if (!navigator.usb) {
    appService.message({ flag: 'error', text: 'webusb_not_supported' });
    throw new Error('WebUSB not supported in this browser');
  }

  let device = null;

  try {
    // Request USB device access
    device = await navigator.usb.requestDevice({
      filters: [
        {
          vendorId: PRINTER_CONFIG.usb.vendorId,
          productId: PRINTER_CONFIG.usb.productId,
        },
        // Also try alternative product IDs
        ...PRINTER_CONFIG.usb.alternativeProductIds.map(productId => ({
          vendorId: PRINTER_CONFIG.usb.vendorId,
          productId,
        })),
      ],
    });

    if (!device) {
      appService.message({ flag: 'error', text: 'printer_not_found' });
      throw new Error('No printer device selected');
    }

    // Open connection
    await device.open();

    // Select configuration (usually configuration 1)
    if (device.configuration === null) {
      await device.selectConfiguration(1);
    }

    // Claim interface (usually interface 0)
    await device.claimInterface(0);

    // Build ESC/POS commands for the DANFE
    const escPosData = buildDanfeEscPos(invoiceData);

    // Find the OUT endpoint (for sending data to printer)
    const interfaceNumber = 0;
    const endpointOut = device.configuration.interfaces[interfaceNumber].alternate.endpoints.find(
      ep => ep.direction === 'out'
    );

    if (!endpointOut) {
      throw new Error('No OUT endpoint found');
    }

    // Send data to printer
    await device.transferOut(endpointOut.endpointNumber, escPosData);

    // Success message
    appService.message({ flag: 'success', text: 'print_sent_to_printer', duration: 2 });

    return true;
  } catch (error) {
    console.error('[PRINT USB] Error:', error);

    // User canceled device selection
    if (error.name === 'NotFoundError') {
      appService.message({ flag: 'warning', text: 'printer_selection_canceled' });
    } else {
      appService.message({ flag: 'error', text: 'print_usb_error' });
    }

    throw error;
  } finally {
    // Close device connection
    if (device) {
      try {
        await device.releaseInterface(0);
        await device.close();
      } catch (e) {
        console.warn('[PRINT USB] Error closing device:', e);
      }
    }
  }
}

/**
 * Build ESC/POS commands for DANFE NFC-e
 * @param {Object} data - Invoice data
 * @returns {Uint8Array} ESC/POS command bytes
 */
function buildDanfeEscPos(data) {
  const commands = [];

  // Helper to add commands
  const add = (...bytes) => commands.push(...bytes.flat());

  // Helper to add text with CP850 encoding (for thermal printers)
  const addText = text => {
    commands.push(...encodeCP850(text || ''));
  };

  // Helper to add line
  const addLine = text => {
    addText(text);
    add(ESC_POS.FEED_LINE);
  };

  // Helper for centered line
  const addCentered = text => {
    add(ESC_POS.ALIGN_CENTER);
    addLine(text);
    add(ESC_POS.ALIGN_LEFT);
  };

  // Helper for bold line
  const addBold = text => {
    add(ESC_POS.TXT_BOLD_ON);
    addLine(text);
    add(ESC_POS.TXT_BOLD_OFF);
  };

  // Helper for separator line
  const addSeparator = (char = '-') => {
    addLine(char.repeat(PRINTER_CONFIG.charsPerLine));
  };

  // Helper for two-column line (label: value)
  const addRow = (label, value, padChar = '.') => {
    const maxLen = PRINTER_CONFIG.charsPerLine;
    const labelLen = label.length;
    const valueLen = String(value).length;
    const padLen = maxLen - labelLen - valueLen - 1;
    const padding = padLen > 0 ? padChar.repeat(padLen) : '';
    addLine(`${label}${padding} ${value}`);
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // BUILD DANFE
  // ─────────────────────────────────────────────────────────────────────────────

  // Clear buffer and initialize printer
  add(ESC_POS.CANCEL); // CAN - Cancel any pending data in buffer
  add(ESC_POS.INIT); // ESC @ - Reset printer and clear buffer
  add(ESC_POS.INIT); // Send twice to ensure clean state
  add(ESC_POS.TXT_NORMAL); // Reset text formatting
  add(ESC_POS.ALIGN_LEFT); // Reset alignment
  add(ESC_POS.CHARSET_PC850); // CP850 - Multilingual Latin I (standard for Brazilian printers)

  // Issuer section (header)
  const issuer = data.issuer || data.issuerEntity || {};
  add(ESC_POS.ALIGN_CENTER);
  add(ESC_POS.TXT_BOLD_ON);
  addLine(issuer.name || issuer.razaoSocial || '');
  add(ESC_POS.TXT_BOLD_OFF);
  // CNPJ | IE on same line
  const cnpj = formatCnpj(issuer.cnpj);
  const ie = issuer.ie || issuer.inscricaoEstadual || 'ISENTO';
  addLine(`${cnpj} | IE: ${ie}`);
  // Full address on one line
  if (issuer.address) {
    const addr = issuer.address;
    const street = addr.address1 || '';
    const number = addr.number || 'S/N';
    const neighborhood = addr.neighborhood || '';
    const city = addr.city || '';
    const region = addr.region || '';
    addLine(`${street}, ${number} - ${neighborhood}`);
    addLine(`${city}/${region}`);
  }
  add(ESC_POS.ALIGN_LEFT);
  addSeparator('-');

  // Items section
  add(ESC_POS.TXT_BOLD_ON);
  addCentered('ITENS');
  add(ESC_POS.TXT_BOLD_OFF);
  addSeparator('-');

  // Items header
  addLine('COD   DESCRICAO');
  addLine('QTD UN   VL.UNIT   VL.TOTAL');
  addSeparator('-');

  // Items list
  const items = data.items || [];
  items.forEach((item, idx) => {
    const code = String(item.productCode || item.code || idx + 1).substring(0, 5);
    const desc = String(item.productName || item.name || item.description || '').substring(0, 35);
    const qty = formatNumber(item.quantity || 1, 3);
    const unit = String(item.unit || 'UN').substring(0, 3);
    const unitPrice = formatCurrency(item.unitPrice || item.price || 0);
    const total = formatCurrency(
      item.totalPrice || item.total || item.quantity * item.unitPrice || 0
    );

    addLine(`${code.padEnd(6)}${desc}`);
    addLine(`${qty} ${unit.padEnd(4)} ${unitPrice.padStart(10)} ${total.padStart(10)}`);
  });

  addSeparator('-');

  // Totals section
  const totals = data.totals || {};
  addRow('Qtd. Itens', items.length);
  addRow('Subtotal', formatCurrency(totals.productsAmount || 0));

  if (totals.discountAmount > 0) {
    addRow('Desconto', formatCurrency(totals.discountAmount));
  }
  if (totals.freightAmount > 0) {
    addRow('Frete', formatCurrency(totals.freightAmount));
  }

  add(ESC_POS.TXT_BOLD_ON);
  add(ESC_POS.TXT_DOUBLE_HEIGHT);
  addRow('TOTAL', formatCurrency(totals.documentAmount || 0));
  add(ESC_POS.TXT_NORMAL);
  add(ESC_POS.TXT_BOLD_OFF);

  // Approximate taxes (Lei 12.741/2012)
  if (totals.approximateTaxes > 0) {
    add(ESC_POS.FEED_LINE);
    add(ESC_POS.ALIGN_CENTER);
    addLine(`Trib.Tot.Inc.(Lei 12.741/12): ${formatCurrency(totals.approximateTaxes)}`);
    add(ESC_POS.ALIGN_LEFT);
  }

  addSeparator('-');

  // Payment section
  add(ESC_POS.TXT_BOLD_ON);
  addCentered('PAGAMENTO');
  add(ESC_POS.TXT_BOLD_OFF);

  const payments = data.payment?.methods || [];
  payments.forEach(payment => {
    const type = getPaymentTypeName(payment.type || payment.code);
    addRow(type, formatCurrency(payment.amount || 0));
  });

  addSeparator('-');

  // Consumer section
  const recipient = data.recipient || data.recipientEntity || {};
  add(ESC_POS.TXT_BOLD_ON);
  addLine('CONSUMIDOR');
  add(ESC_POS.TXT_BOLD_OFF);

  if (recipient.cpf || recipient.cnpj) {
    addLine(`CPF/CNPJ: ${formatCpfCnpj(recipient.cpf || recipient.cnpj)}`);
  } else {
    addLine('CONSUMIDOR NAO IDENTIFICADO');
  }
  if (recipient.name) {
    addLine(recipient.name.substring(0, PRINTER_CONFIG.charsPerLine));
  }

  addSeparator('-');

  // Fiscal info section
  add(ESC_POS.TXT_BOLD_ON);
  addCentered('INFORMACOES FISCAIS');
  add(ESC_POS.TXT_BOLD_OFF);

  addRow('Numero', data.documentNumber || '-');
  addRow('Serie', data.documentSerie || '1');
  addRow('Data', formatDate(data.issueDate || data.createdAt));

  // Access key
  if (data.accessKey) {
    add(ESC_POS.FEED_LINE);
    addCentered('CHAVE DE ACESSO');
    // Split access key into groups of 4 for readability
    const key = data.accessKey.replace(/\D/g, '');
    const keyFormatted = key.match(/.{1,4}/g)?.join(' ') || key;
    add(ESC_POS.ALIGN_CENTER);
    addLine(keyFormatted.substring(0, PRINTER_CONFIG.charsPerLine));
    if (keyFormatted.length > PRINTER_CONFIG.charsPerLine) {
      addLine(keyFormatted.substring(PRINTER_CONFIG.charsPerLine));
    }
    add(ESC_POS.ALIGN_LEFT);
  }

  // QR Code (if supported and available)
  if (data.qrCodeUrl || data.qrCode) {
    add(ESC_POS.FEED_LINE);
    addCentered('Consulte pela Chave de Acesso em');
    addCentered('www.nfce.fazenda.sp.gov.br');
    add(ESC_POS.FEED_LINE);

    // QR Code ESC/POS commands (URL is ASCII, so encodeCP850 works fine)
    const qrData = data.qrCodeUrl || data.qrCode;
    const qrBytes = encodeCP850(qrData);

    add(ESC_POS.ALIGN_CENTER);
    add(ESC_POS.QRCODE_MODEL);
    add(ESC_POS.QRCODE_SIZE(6)); // Size 6 (medium)
    add(ESC_POS.QRCODE_ERROR);
    add(ESC_POS.QRCODE_STORE(Array.from(qrBytes)));
    add(ESC_POS.QRCODE_PRINT);
    add(ESC_POS.ALIGN_LEFT);
  }

  // Protocol
  if (data.protocol) {
    add(ESC_POS.FEED_LINE);
    addCentered('PROTOCOLO DE AUTORIZACAO');
    addCentered(data.protocol);
    addCentered(formatDateTime(data.authorizedAt || data.protocolDate));
  }

  // Homologation warning
  if (data.environment === 'homologation') {
    add(ESC_POS.FEED_LINE);
    addSeparator('*');
    add(ESC_POS.TXT_BOLD_ON);
    addCentered('AMBIENTE DE HOMOLOGACAO');
    addCentered('SEM VALOR FISCAL');
    add(ESC_POS.TXT_BOLD_OFF);
    addSeparator('*');
  }

  // Feed lines and cut
  add(ESC_POS.FEED_LINES(PRINTER_CONFIG.settings.feedLines));

  // Open cash drawer if configured
  if (PRINTER_CONFIG.settings.openDrawer) {
    add(ESC_POS.DRAWER_OPEN_PIN2);
  }

  // Beep if configured
  if (PRINTER_CONFIG.settings.beep) {
    add(ESC_POS.BEEP);
  }

  // Cut paper if configured
  if (PRINTER_CONFIG.settings.cutPaper) {
    add(ESC_POS.CUT_PAPER_FEED);
  }

  return new Uint8Array(commands);
}

/**
 * Print DANFE using browser print dialog (iframe method)
 * @param {string} elementId - DOM element ID containing the DANFE content
 * @returns {Promise<boolean>} Success status
 */
async function printDanfeBrowser(elementId = 'danfe-print-container') {
  return new Promise((resolve, reject) => {
    // Get the DANFE container element
    const danfeElement = document.getElementById(elementId);
    if (!danfeElement) {
      console.error('[PRINT] Element not found:', elementId);
      appService.message({ flag: 'error', text: 'print_element_not_found' });
      reject(new Error('Print element not found'));
      return;
    }

    // Clone the element to manipulate without affecting the original
    const clonedElement = danfeElement.cloneNode(true);

    // Convert QR Code SVG to base64 image for proper printing
    const qrCodeSvg = clonedElement.querySelector('.danfe-qrcode svg');
    if (qrCodeSvg) {
      try {
        const svgData = new XMLSerializer().serializeToString(qrCodeSvg);
        const svgBase64 = btoa(unescape(encodeURIComponent(svgData)));
        const imgSrc = `data:image/svg+xml;base64,${svgBase64}`;

        // Replace SVG with IMG
        const img = document.createElement('img');
        img.src = imgSrc;
        img.width = 100;
        img.height = 100;
        img.style.cssText = 'width: 100px; height: 100px;';
        qrCodeSvg.parentNode.replaceChild(img, qrCodeSvg);
      } catch (e) {
        console.warn('[PRINT] Could not convert QR Code SVG:', e);
      }
    }

    // Remove no-print elements and buttons from clone
    clonedElement
      .querySelectorAll('.no-print, .no-print-link, button, .ant-btn')
      .forEach(el => el.remove());

    // Remove existing print iframe if any
    const existingIframe = document.getElementById('danfe-print-iframe');
    if (existingIframe) {
      existingIframe.remove();
    }

    // Create hidden iframe for printing
    const iframe = document.createElement('iframe');
    iframe.id = 'danfe-print-iframe';
    iframe.style.cssText = 'position: absolute; width: 0; height: 0; border: none; left: -9999px;';
    document.body.appendChild(iframe);

    // Build HTML for print
    const printContent = buildPrintHtml(clonedElement.innerHTML);

    // Write content to iframe
    const iframeDoc = iframe.contentWindow || iframe.contentDocument;
    const doc = iframeDoc.document || iframeDoc;
    doc.open();
    doc.write(printContent);
    doc.close();

    // Wait for content to load then print
    iframe.onload = () => {
      // Small delay to ensure images are loaded
      setTimeout(() => {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();

          // Remove iframe after print dialog closes
          setTimeout(() => {
            iframe.remove();
            resolve(true);
          }, 1000);
        } catch (error) {
          console.error('[PRINT] Error during print:', error);
          iframe.remove();
          reject(error);
        }
      }, 100);
    };
  });
}

/**
 * Print a test page using browser print dialog
 * Used to test thermal printer with system drivers
 * @returns {Promise<boolean>} Success status
 */
async function printTestPage() {
  return new Promise((resolve, reject) => {
    // Create test page HTML
    const testHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Teste de Impressao</title>
        <style>
          @page { size: 80mm auto; margin: 0; }
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: 'Courier New', Courier, monospace;
            font-size: 12px;
            width: 80mm;
            padding: 4mm;
            line-height: 1.5;
          }
          h1 { font-size: 16px; text-align: center; margin-bottom: 10px; }
          .divider { border-top: 1px dashed #000; margin: 10px 0; }
          .center { text-align: center; }
          .row { display: flex; justify-content: space-between; }
          table { width: 100%; border-collapse: collapse; margin: 8px 0; }
          th, td { text-align: left; padding: 2px 0; }
          th { border-bottom: 1px solid #000; }
          .total { font-weight: bold; font-size: 14px; }
        </style>
      </head>
      <body>
        <h1>TESTE DE IMPRESSAO</h1>
        <p class="center">Impressora Termica</p>
        <div class="divider"></div>

        <p class="center"><strong>EMPRESA TESTE LTDA</strong></p>
        <p class="center">CNPJ: 00.000.000/0001-00</p>
        <p class="center">Rua Teste, 123 - Centro</p>
        <p class="center">Sao Paulo - SP</p>

        <div class="divider"></div>

        <table>
          <thead>
            <tr><th>Item</th><th>Qtd</th><th>Valor</th></tr>
          </thead>
          <tbody>
            <tr><td>Produto Teste 1</td><td>2</td><td>R$ 10,00</td></tr>
            <tr><td>Produto Teste 2</td><td>1</td><td>R$ 15,00</td></tr>
            <tr><td>Produto Teste 3</td><td>3</td><td>R$ 5,00</td></tr>
          </tbody>
        </table>

        <div class="divider"></div>

        <div class="row"><span>Subtotal:</span><span>R$ 40,00</span></div>
        <div class="row"><span>Desconto:</span><span>R$ 0,00</span></div>
        <div class="row total"><span>TOTAL:</span><span>R$ 40,00</span></div>

        <div class="divider"></div>

        <p class="center">Pagamento: Dinheiro</p>
        <p class="center">Troco: R$ 0,00</p>

        <div class="divider"></div>

        <p class="center" style="font-size: 10px;">
          Se este cupom foi impresso<br/>
          corretamente, sua impressora<br/>
          esta funcionando!
        </p>

        <p class="center" style="margin-top: 10px; font-size: 9px;">
          ${new Date().toLocaleString('pt-BR')}
        </p>
      </body>
      </html>
    `;

    // Remove existing test iframe if any
    const existingIframe = document.getElementById('test-print-iframe');
    if (existingIframe) {
      existingIframe.remove();
    }

    // Create hidden iframe for printing
    const iframe = document.createElement('iframe');
    iframe.id = 'test-print-iframe';
    iframe.style.cssText = 'position: absolute; width: 0; height: 0; border: none; left: -9999px;';
    document.body.appendChild(iframe);

    // Write content to iframe
    const iframeDoc = iframe.contentWindow || iframe.contentDocument;
    const doc = iframeDoc.document || iframeDoc;
    doc.open();
    doc.write(testHtml);
    doc.close();

    // Wait for content to load then print
    iframe.onload = () => {
      setTimeout(() => {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();

          // Remove iframe after print dialog closes
          setTimeout(() => {
            iframe.remove();
            resolve(true);
          }, 1000);
        } catch (error) {
          console.error('[PRINT] Test print error:', error);
          iframe.remove();
          reject(error);
        }
      }, 100);
    };
  });
}

/**
 * Print DANFE via network to thermal printer
 * Uses html2canvas to convert the preview to an image and sends to backend
 * @param {string} elementId - DOM element ID containing the DANFE content
 * @returns {Promise<boolean>} Success status
 */
async function printDanfeNetwork(elementId = 'danfe-print-container') {
  // Get network printer configuration
  const printerIp = getStorageModuleItem('nfceNetworkPrinterIp');
  const printerPort = getStorageModuleItem('nfceNetworkPrinterPort') || 9100;

  if (!printerIp) {
    appService.message({ flag: 'error', text: 'network_printer_not_configured' });
    throw new Error('Network printer IP not configured');
  }

  // Get the DANFE container element
  const danfeElement = document.getElementById(elementId);
  if (!danfeElement) {
    console.error('[PRINT NETWORK] Element not found:', elementId);
    appService.message({ flag: 'error', text: 'print_element_not_found' });
    throw new Error('Print element not found');
  }

  appService.message({ flag: 'info', text: 'preparing_print', duration: 2 });

  try {
    // Dynamically import html2canvas
    const html2canvas = (await import('html2canvas')).default;

    // Convert HTML to canvas
    const canvas = await html2canvas(danfeElement, {
      scale: 2, // Higher quality
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      width: 384, // 80mm at 203 DPI (common for thermal printers)
    });

    // Convert canvas to base64 PNG
    const imageBase64 = canvas.toDataURL('image/png').split(',')[1];

    // Send to backend for printing
    const [response] = await axiosService.post({
      url: `${API.BASE_URL}/print/thermal/image`,
      body: {
        printerIp,
        printerPort: parseInt(printerPort, 10),
        imageBase64,
        width: 384,
      },
    });

    if (response?.success) {
      appService.message({ flag: 'success', text: 'print_sent_to_printer', duration: 2 });
      return true;
    } else {
      throw new Error(response?.error || 'Print failed');
    }
  } catch (error) {
    console.error('[PRINT NETWORK] Error:', error);
    appService.message({ flag: 'error', text: 'network_print_error' });
    throw error;
  }
}

/**
 * Test network printer connection
 * @param {Object} options - Test options
 * @param {string} options.printerIp - Printer IP address
 * @param {number} [options.printerPort=9100] - Printer port
 * @param {boolean} [options.printTestPage=false] - Print a test page
 * @returns {Promise<Object>} Test result
 */
async function testNetworkPrinter(options = {}) {
  const { printerIp, printerPort = 9100, printTestPage = false } = options;

  if (!printerIp) {
    return { success: false, error: 'Printer IP is required' };
  }

  try {
    const [response] = await axiosService.post({
      url: `${API.BASE_URL}/print/thermal/test`,
      body: {
        printerIp,
        printerPort: parseInt(printerPort, 10),
        printTestPage,
      },
    });

    return response || { success: false, error: 'No response from server' };
  } catch (error) {
    console.error('[PRINT NETWORK] Test error:', error);
    return { success: false, error: error.message };
  }
}

// *******************************************************************************
// HELPERS
// *******************************************************************************

/**
 * Build HTML content for print window
 * @param {string} content - Inner HTML content
 * @returns {string} Complete HTML document for printing
 */
function buildPrintHtml(content) {
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <title>DANFE NFC-e</title>
      <style>
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        /* Page size for thermal printer 80mm */
        @page {
          size: 80mm auto;
          margin: 0;
        }
        html {
          height: 100%;
        }
        body {
          font-family: 'Courier New', Courier, monospace;
          font-size: 11px;
          width: 80mm;
          max-width: 80mm;
          min-width: 80mm;
          margin: 0 auto;
          padding: 4mm;
          background: white;
          color: #000;
          line-height: 1.4;
        }
        /* Center on A4 preview */
        @media print {
          html {
            height: 100%;
          }
          body {
            width: 80mm !important;
            max-width: 80mm !important;
            min-width: 80mm !important;
            margin: 0 auto !important;
          }
        }

        /* Header */
        .danfe-header {
          text-align: center;
          margin-bottom: 12px;
        }
        .danfe-header h5,
        .danfe-header .ant-typography {
          font-size: 14px !important;
          font-weight: 700;
          margin: 0;
          color: #000;
        }
        .danfe-header span {
          font-size: 10px;
          color: #000;
          display: block;
          margin-top: 4px;
        }

        /* Dividers */
        .ant-divider {
          margin: 10px 0;
          border-top: 1px solid #d9d9d9;
        }
        .ant-divider-dashed {
          border-top-style: dashed;
        }

        /* Sections */
        .danfe-section {
          margin: 8px 0;
        }

        /* Issuer Section */
        .danfe-issuer {
          text-align: center;
        }
        .danfe-issuer h5,
        .danfe-issuer .ant-typography {
          font-size: 12px;
          font-weight: 700;
          margin: 0 0 4px 0;
          color: #000;
        }
        .danfe-issuer span,
        .danfe-issuer p {
          font-size: 10px;
          margin: 2px 0;
          color: #000;
          display: block;
        }

        /* Items Section */
        .danfe-items {
          margin: 8px 0;
        }
        .danfe-items-header {
          font-weight: bold;
          font-size: 11px;
          margin-bottom: 6px;
        }
        .danfe-items-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 10px;
        }
        .danfe-items-table th {
          padding: 4px 2px;
          text-align: left;
          border-bottom: 1px solid #d9d9d9;
          font-weight: 600;
          font-size: 10px;
        }
        .danfe-items-table td {
          padding: 4px 2px;
          border-bottom: 1px dashed #e8e8e8;
          vertical-align: top;
        }
        .danfe-items-table tbody tr:last-child td {
          border-bottom: none;
        }
        .danfe-item-description {
          max-width: 100px;
        }
        .danfe-item-code {
          font-size: 8px;
          color: #000;
          display: block;
        }
        .text-center { text-align: center; }
        .text-right { text-align: right; }
        .danfe-items-count {
          text-align: right;
          font-size: 10px;
          color: #000;
          margin-top: 4px;
        }

        /* Totals Section */
        .danfe-totals {
          margin: 8px 0;
        }
        .danfe-totals-block {
          margin-bottom: 8px;
        }
        .danfe-totals-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 3px 0;
          font-size: 11px;
        }
        .danfe-totals-total {
          border-top: 1px solid #d9d9d9;
          padding-top: 6px;
          margin-top: 6px;
          font-weight: bold;
          font-size: 13px;
        }
        .danfe-totals-label {
          font-weight: bold;
          font-size: 11px;
          margin-bottom: 4px;
          display: block;
        }

        /* Consumer Section */
        .danfe-consumer {
          margin: 8px 0;
        }
        .danfe-consumer-label {
          font-weight: bold;
          font-size: 11px;
          margin-bottom: 4px;
        }

        /* Fiscal Info Section */
        .danfe-fiscal-info {
          margin: 8px 0;
        }
        .danfe-doc-info {
          margin-bottom: 8px;
        }
        .danfe-doc-info-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 4px;
          font-size: 11px;
        }
        .danfe-access-key {
          background: #f5f5f5;
          padding: 8px;
          border-radius: 4px;
          text-align: center;
          margin: 8px 0;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        .danfe-access-key span {
          font-size: 10px;
          font-family: monospace;
          word-break: break-all;
          display: block;
        }
        .danfe-access-key strong {
          display: block;
          margin-bottom: 4px;
        }

        /* QR Code */
        .danfe-qrcode {
          margin: 12px 0;
          text-align: center;
        }
        .danfe-qrcode > div {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 16px;
        }
        .danfe-qrcode img,
        .danfe-qrcode svg {
          width: 100px !important;
          height: 100px !important;
        }
        .danfe-qrcode-text {
          text-align: left;
          font-size: 10px;
        }

        /* Protocol */
        .danfe-protocol {
          text-align: center;
          padding-top: 8px;
          border-top: 1px dashed #d9d9d9;
          margin-top: 8px;
          font-size: 10px;
        }

        /* Homologation Warning */
        .danfe-homologation {
          margin-top: 12px;
          padding: 8px;
          background: #fff7e6;
          border: 1px solid #ffd591;
          border-radius: 4px;
          text-align: center;
          font-size: 10px;
          color: #000;
          font-weight: 700;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }

        /* Global text styles for print - ABSOLUTE BLACK + BOLD for legibility */
        * {
          color: #000 !important;
          font-weight: 700 !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }

        /* Ensure all text elements are bold */
        body, div, span, p, td, th, label, a,
        h1, h2, h3, h4, h5, h6,
        strong, b,
        .ant-typography,
        .ant-typography-secondary {
          color: #000 !important;
          font-weight: 700 !important;
        }

        /* Hide interactive elements (copy icons, links, buttons, nav controls) */
        .no-print,
        .no-print-link,
        .ant-typography-copy,
        .ant-typography-copy-success,
        .anticon-copy,
        .anticon-check,
        .anticon-link,
        .anticon-menu-fold,
        .anticon-menu-unfold,
        .nav-icon,
        [class*="NavItem"],
        [class*="nav-toggle"],
        [class*="anticon-link"],
        button,
        .ant-btn,
        a[href],
        a.ant-btn {
          display: none !important;
          visibility: hidden !important;
        }

        /* Show print-only elements */
        .print-only {
          display: block !important;
          visibility: visible !important;
        }

        /* Watermark */
        .danfe-watermark {
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%) rotate(-45deg);
          font-size: 48px;
          font-weight: bold;
          color: rgba(0, 0, 0, 0.06);
          pointer-events: none;
          z-index: 1;
          white-space: nowrap;
        }

        /* Typography helpers */
        strong, b { font-weight: bold; }
        .ant-typography { margin: 0; }

        /* Hide copy buttons and links in print */
        .ant-typography-copy,
        .ant-btn,
        button {
          display: none !important;
        }

        /* Print-only text */
        .print-only {
          display: block !important;
        }
      </style>
    </head>
    <body>
      ${content}
    </body>
    </html>
  `;
}

/**
 * Get color for invoice type tag
 * @param {string} type - Invoice type (nfe, nfce, nfse)
 * @returns {string} Color string for Ant Design Tag
 */
function getInvoiceTypeColor(type) {
  const colors = {
    nfe: 'blue',
    nfce: 'green',
    nfse: 'purple',
  };

  return colors[type] || 'default';
}

/**
 * Format CNPJ for display
 * @param {string} cnpj - CNPJ number
 * @returns {string} Formatted CNPJ (XX.XXX.XXX/XXXX-XX)
 */
function formatCnpj(cnpj) {
  if (!cnpj) return '';
  const digits = String(cnpj).replace(/\D/g, '');
  if (digits.length !== 14) return cnpj;
  return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
}

/**
 * Format CPF or CNPJ for display
 * @param {string} value - CPF or CNPJ number
 * @returns {string} Formatted CPF/CNPJ
 */
function formatCpfCnpj(value) {
  if (!value) return '';
  const digits = String(value).replace(/\D/g, '');
  if (digits.length === 11) {
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }
  if (digits.length === 14) {
    return formatCnpj(digits);
  }
  return value;
}

/**
 * Format number for display
 * @param {number} value - Number to format
 * @param {number} [decimals=2] - Decimal places
 * @returns {string} Formatted number
 */
function formatNumber(value, decimals = 2) {
  return Number(value || 0).toFixed(decimals);
}

/**
 * Format currency for display (Brazilian Real)
 * @param {number} value - Value to format
 * @returns {string} Formatted currency (R$ X.XXX,XX)
 */
function formatCurrency(value) {
  return Number(value || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}

/**
 * Format date for display
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted date (DD/MM/YYYY)
 */
function formatDate(date) {
  if (!date) return '';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('pt-BR');
}

/**
 * Format date and time for display
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted datetime (DD/MM/YYYY HH:MM:SS)
 */
function formatDateTime(date) {
  if (!date) return '';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString('pt-BR');
}

/**
 * Get payment type name for display
 * @param {string|number} code - Payment type code
 * @returns {string} Payment type name
 */
function getPaymentTypeName(code) {
  const types = {
    '01': 'Dinheiro',
    '02': 'Cheque',
    '03': 'Cartao Credito',
    '04': 'Cartao Debito',
    '05': 'Credito Loja',
    10: 'Vale Alimentacao',
    11: 'Vale Refeicao',
    12: 'Vale Presente',
    13: 'Vale Combustivel',
    14: 'Duplicata Mercantil',
    15: 'Boleto Bancario',
    16: 'Deposito Bancario',
    17: 'PIX',
    18: 'Transf. Bancaria',
    19: 'Fidelidade/Cashback',
    90: 'Sem Pagamento',
    99: 'Outros',
    // String codes
    money: 'Dinheiro',
    cash: 'Dinheiro',
    credit: 'Cartao Credito',
    credit_card: 'Cartao Credito',
    debit: 'Cartao Debito',
    debit_card: 'Cartao Debito',
    pix: 'PIX',
    transfer: 'Transf. Bancaria',
    boleto: 'Boleto',
    check: 'Cheque',
    voucher: 'Vale',
    other: 'Outros',
  };
  return types[String(code).toLowerCase()] || types[code] || 'Outros';
}

function isProcessedBySefaz(status) {
  return ['authorized', 'canceled', 'denied'].includes(status);
}

// *******************************************************************************
// EMISSION VALIDATION
// *******************************************************************************

/**
 * Validate if the issuer company is eligible to emit fiscal documents
 * Uses backend API to validate:
 * 1. If company has a valid digital certificate (A1)
 * 2. If the specific document type (NFCe, NFe, NFSe) is enabled and validated
 *
 * @param {Object} options - Validation options
 * @param {string} [options.invoiceId] - Invoice ID to get document type and issuer
 * @param {Object} [options.invoice] - Invoice object (if already loaded, to avoid extra fetch)
 * @returns {Promise<{valid: boolean, error?: string, errorCode?: string}>} Validation result
 */
async function validateEmissionEligibility(options = {}) {
  const { invoiceId } = options;
  let { invoice } = options;

  try {
    // ─────────────────────────────────────────────────────────────────────────
    // STEP 0: Get invoice if not provided
    // ─────────────────────────────────────────────────────────────────────────
    if (!invoice && invoiceId) {
      invoice = await getById(invoiceId);
    }

    if (!invoice) {
      appService.notification({
        flag: 'error',
        title: 'emission_validation_error',
        description: 'invoice_not_found',
      });
      return { valid: false, error: 'Invoice not found', errorCode: 'INVOICE_NOT_FOUND' };
    }

    // Get issuer ID from invoice
    const issuerId = invoice?.issuer?._id || invoice?.issuer?.entityId || invoice?.issuerId;
    const documentType = invoice?.documentType?.toLowerCase(); // nfce, nfe, nfse

    if (!issuerId) {
      appService.notification({
        flag: 'error',
        title: 'emission_validation_error',
        description: 'issuer_not_found',
      });
      return { valid: false, error: 'Issuer not found', errorCode: 'ISSUER_NOT_FOUND' };
    }

    // ─────────────────────────────────────────────────────────────────────────
    // STEP 1: Call backend API to get validation status
    // ─────────────────────────────────────────────────────────────────────────
    const url = API.FISCAL.SETUP_VALIDATION_STATUS.replace(':entityId', issuerId);
    const [response] = await axiosService.get({ url, returnAsObject: true });

    if (!response?.success) {
      appService.notification({
        flag: 'error',
        title: 'emission_validation_error',
        description: 'validation_status_error',
      });
      return { valid: false, error: 'Failed to get validation status', errorCode: 'API_ERROR' };
    }

    // ─────────────────────────────────────────────────────────────────────────
    // VALIDATION 1: Check digital certificate
    // ─────────────────────────────────────────────────────────────────────────
    const certificate = response?.certificate || {};
    const hasCertificate = certificate?.hasUploaded && certificate?.status === 'valid';

    if (!hasCertificate) {
      // Show informative modal about missing certificate
      appService.modal({
        flag: 'warning',
        title: 'digital_certificate_not_found',
        content: 'digital_certificate_not_configured',
        okText: 'understood',
        centered: true,
      });

      return {
        valid: false,
        error: 'Digital certificate not found or invalid',
        errorCode: 'CERTIFICATE_NOT_FOUND',
      };
    }

    // ─────────────────────────────────────────────────────────────────────────
    // VALIDATION 2: Check if document type is enabled
    // ─────────────────────────────────────────────────────────────────────────
    if (documentType) {
      const services = response?.services || {};
      const serviceStatus = services[documentType]; // services.nfce, services.nfe, services.nfse

      if (!serviceStatus?.isEnabled) {
        const docTypeName = translateX(documentType);

        // Show informative modal about document type not enabled
        appService.modal({
          flag: 'warning',
          title: 'resource_not_configured',
          content: translateX('document_type_not_enabled_modal_description', {
            documentType: docTypeName,
          }),
          okText: 'understood',
          centered: true,
        });

        return {
          valid: false,
          error: `${docTypeName} not enabled for this issuer`,
          errorCode: 'DOCUMENT_TYPE_NOT_ENABLED',
        };
      }

      // If enabled, it means validation with provider was successful
      // No need to check CSC separately - backend already validated it
    }

    // ─────────────────────────────────────────────────────────────────────────
    // ALL VALIDATIONS PASSED
    // ─────────────────────────────────────────────────────────────────────────
    return { valid: true };
  } catch (error) {
    console.error('[invoicesService.validateEmissionEligibility] Error:', error);

    appService.notification({
      flag: 'error',
      title: 'emission_validation_error',
      description: error?.message || 'unknown_error',
      noTranslateDescription: !!error?.message,
    });

    return {
      valid: false,
      error: error?.message || 'Validation error',
      errorCode: 'VALIDATION_ERROR',
    };
  }
}

/**
 * Validate invoice data (internal validation)
 * Validates the invoice structure, items, payments, taxes, etc.
 * @param {string} invoiceId - Invoice ID to validate
 * @param {Object} options - Validation options
 * @param {string} [options.level='internal'] - Validation level
 * @param {boolean} [options.updateDocuments=true] - Update document with validation result
 * @returns {Promise<Object>} Validation result
 */
async function validate(invoiceId, options = {}) {
  const { level = 'internal', updateDocuments = true } = options;

  const msg = await appService.message({
    flag: 'loading',
    text: 'validating_invoice',
    duration: 0,
  });

  try {
    const [response] = await axiosService.post({
      url: API.INVOICES.VALIDATE,
      data: {
        ids: [invoiceId],
        level,
        updateDocuments,
      },
      returnAsObject: true,
    });

    const details = response?.details || [];
    const detail = details[0] || {};

    if (detail?.isValid) {
      msg.update({
        flag: 'success',
        text: 'invoice_validated_successfully',
        duration: 3,
      });
    } else {
      msg.update({
        flag: 'error',
        text: 'validation_failed',
        duration: 4,
      });
    }

    return {
      success: detail?.isValid || false,
      isValid: detail?.isValid || false,
      errors: detail?.errors || [],
      warnings: detail?.warnings || [],
      documentId: detail?.documentId,
    };
  } catch (error) {
    console.error('[invoicesService.validate] Error:', error);

    msg.update({
      flag: 'error',
      text: 'validation_failed',
      duration: 4,
    });

    const errorMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      'Validation error';

    return {
      success: false,
      isValid: false,
      error: errorMessage,
      errors: [{ message: errorMessage }],
    };
  }
}

/**
 * Export XMLs as ZIP file for a given month
 * @param {Object} params - Export parameters
 * @param {string} params.month - Month in YYYY-MM format
 * @param {string} params.documentType - Document type: 'nfe', 'nfce', or 'all'
 * @param {string} [params.companyId] - Company/issuer ID for multi-company filtering
 * @param {string} [params.companyCnpj] - Company CNPJ for filename
 * @returns {Promise<boolean>} True if successful
 */
async function exportXmlBatch({ month, documentType, companyId, companyCnpj }) {
  const msg = await appService.message({
    flag: 'loading',
    text: 'generating_zip',
    delay: 0,
  });

  try {
    // Use native fetch to avoid axios interceptor transforming the blob
    const useZustand = (await import('hooks/useZustand')).default;
    const token = useZustand('auth', 'token', false);
    const subscription = useZustand('auth', 'subscription', false);
    const accountId = useZustand('auth', 'accountId', false);

    if (!token) {
      throw new Error('No authentication token found');
    }

    const { default: appCfg } = await import('configs/ConfigIndex');
    const { getUrlFromBrowser } = await import('utils/helpers');

    const isLocal = appCfg.CONNECTION === 'local';
    const protocol = isLocal ? 'http' : 'https';
    const host = isLocal ? appCfg.API_HOST_LOCALHOST : appCfg.API_HOST_PRODUCTION;
    const port = appCfg.HOMOLOGATION_MODE
      ? appCfg.API_PORT_HOMOLOGATION
      : appCfg.API_PORT_PRODUCTION;
    const baseUrl = `${protocol}://${host}:${port}/api`;

    const payload = { month, documentType };
    if (companyId) {
      payload.companyId = companyId;
    }

    const response = await fetch(`${baseUrl}${API.INVOICES.EXPORT_XML}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: token,
        'x-application': appCfg.ID,
        'x-token': appCfg.TOKEN,
        'x-clienturl': getUrlFromBrowser(),
        'x-accountid': accountId || '',
        'x-subscriptionid': subscription?._id || '',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const blob = await response.blob();

    // Build filename with CNPJ if available
    const prefix = documentType === 'all' ? 'xml' : `${documentType}-xml`;
    const cnpjPart = companyCnpj ? `-${companyCnpj}` : '';
    const filename = `${prefix}${cnpjPart}-${month}.zip`;

    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);

    msg.update({
      flag: 'success',
      text: 'download_zip_success',
      delay: 2,
    });

    return true;
  } catch (error) {
    console.error('[exportXmlBatch] Error:', error);
    msg.update({
      flag: 'error',
      text: error?.message || translateX('error_generating_zip'),
      delay: 5,
    });

    return false;
  }
}

// *******************************************************************************
// SERVICE
// *******************************************************************************
const invoicesService = {
  // CRUD
  getById,
  getPaginated,
  create,
  update,
  remove,

  // Fiscal operations
  emit,
  emitNfce,
  emitNfe,
  emitNfse,
  cancel,
  validate,
  validateEmissionEligibility,
  getPdf,
  getXml,
  downloadDanfe,
  downloadXml,
  printNfceDanfeToPdf,

  // Print operations
  printDanfe,
  printDanfeSerial,
  printDanfeDirectUSB,
  printDanfeBrowser,
  printDanfeNetwork,
  printTestPage,
  testNetworkPrinter,

  // Batch operations
  archiveMany,
  deleteMany,
  exportXmlBatch,

  // Helpers
  getInvoiceTypeColor,
  formatCnpj,
  formatCpfCnpj,
  formatCurrency,
  formatDate,
  formatDateTime,
  getPaymentTypeName,
  isProcessedBySefaz,
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default invoicesService;
